// =============================================================
// FLOW (Wave 6): Settings / Dictionaries hub — deepened.
//
// Ports the prototype SecDictionaries (scripts/perpetuum-desktop-sections5.jsx)
// into ONE reusable managed-list shell over the REAL /api/v1, with the
// device-category CUSTOM-FIELD SCHEMA EDITOR as the centrepiece (the 8-type
// engine: text/email/date/number/select/phone/url/textarea).
//
// HONESTY about the backing API (verified against the DB schema + routes):
//   BACKED-CRUD (list + create + edit + delete), each gated by its REAL module:
//     - Device categories   → /categories          (module: dev)
//                              + custom-field defs   → /custom-field-defs (dev)
//     - Warehouses / stocks  → /stocks              (module: spares)
//     - Downtime categories  → /downtime-categories (module: downtime)
//   READ-ONLY (a fixed enum, no master table):
//     - Task stages          → the fixed TaskStatus enum (open … postponed)
//   NOT CONFIGURABLE YET (no tenant master table / CRUD route at all):
//     - Device manufacturers, Spare categories, Spare manufacturers,
//       Task groups, Report statuses, Checklist presets.
//   We render the last two groups truthfully — never a fake editable list.
//
// custom_field_defs columns (DB-exact): { categoryId, key, label,
//   type (the 8-type enum), required (bool), suffix (text|null) }. There is NO
// options column and NO /custom-field-options route, so a `select` field's
// option VALUES cannot be persisted yet — the editor says so plainly rather
// than inventing a column.
//
// The "Navigation" card (nav layout / sidebar width) is unchanged.
// =============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { usePrefs } from "../app/PrefsContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import {
  useAddCustomFieldOption,
  useCollection,
  useCreateRow,
  useCustomFieldOptions,
  useDeleteCustomFieldOption,
  useDeleteRow,
  useUpdateRow,
  type Row,
} from "../lib/queries.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import type { PermModule } from "@perpetuum/core";

// ---------------------------------------------------------------------------
// Dictionary classification
// ---------------------------------------------------------------------------

/** How a dictionary group is backed (drives what UI we can honestly show). */
type DictKind = "backed" | "readonly" | "unbacked";

interface DictGroup {
  id: string;
  /** i18n key for the group label. */
  labelKey: string;
  hintKey?: string;
  kind: DictKind;
  /** Backed groups: the /api/v1 collection name. */
  collection?: string;
  /** Backed groups: the RBAC module the SERVER enforces for this collection. */
  module?: PermModule;
  /** Device categories carry the custom-field schema editor. */
  schema?: boolean;
  /** stocks has an extra "Main" column. */
  hasMainFlag?: boolean;
}

const DICT_GROUPS: DictGroup[] = [
  { id: "devcat", labelKey: "settings.dict.devcat", hintKey: "settings.dict.devcatHint", kind: "backed", collection: "categories", module: "dev", schema: true },
  { id: "stocks", labelKey: "settings.dict.stocks", hintKey: "settings.dict.stocksHint", kind: "backed", collection: "stocks", module: "spares", hasMainFlag: true },
  { id: "downcat", labelKey: "settings.dict.downcat", hintKey: "settings.dict.downcatHint", kind: "backed", collection: "downtime-categories", module: "downtime" },
  { id: "taskstage", labelKey: "settings.dict.taskstage", hintKey: "settings.dict.taskstageHint", kind: "readonly" },
  { id: "devmfr", labelKey: "settings.dict.devmfr", kind: "unbacked" },
  { id: "sparecat", labelKey: "settings.dict.sparecat", kind: "unbacked" },
  { id: "sparemfr", labelKey: "settings.dict.sparemfr", kind: "unbacked" },
  { id: "taskgrp", labelKey: "settings.dict.taskgrp", kind: "unbacked" },
  { id: "reportstatus", labelKey: "settings.dict.reportstatus", kind: "unbacked" },
  { id: "checklist", labelKey: "settings.dict.checklist", kind: "unbacked" },
];

/** The 8 Perpetuum custom-field types — EXACTLY the DB `type` enum. */
const FIELD_TYPES = ["text", "email", "date", "number", "select", "phone", "url", "textarea"] as const;
type FieldType = (typeof FIELD_TYPES)[number];

/** The fixed work-order status enum (read-only — no master table). */
const TASK_STATUSES = ["open", "in_progress", "paused", "completed", "cancelled", "postponed"] as const;

// ---------------------------------------------------------------------------
// Shared: focus-trapped + Esc modal shell (mirrors Access.tsx Modal)
// ---------------------------------------------------------------------------

function Modal({
  title,
  icon = ICONS.cog,
  onClose,
  children,
  testId,
  width = 480,
}: {
  title: string;
  icon?: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node
      .querySelector<HTMLElement>('input, select, textarea, button, [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }, []);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key !== "Tab") return;
          const focusables = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>(
              'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.hasAttribute("disabled"));
          if (focusables.length === 0) return;
          const first = focusables[0]!;
          const last = focusables[focusables.length - 1]!;
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="modal-hd">
          <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
            <Icon d={icon} size={20} />
          </div>
          <h3>{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );
}

const selectStyle = {
  border: 0,
  background: "none",
  outline: "none",
  font: "inherit",
  color: "inherit",
  width: "100%",
} as const;

function toggleStyle(on: boolean) {
  return {
    width: 38,
    height: 22,
    borderRadius: 999,
    border: 0,
    cursor: "pointer",
    padding: 2,
    background: on ? "var(--brand-primary-fill)" : "var(--sys-bg-sunken)",
    display: "inline-flex",
    justifyContent: on ? "flex-end" : "flex-start",
    alignItems: "center",
    transition: "background .15s",
  } as const;
}

// ---------------------------------------------------------------------------
// Settings root
// ---------------------------------------------------------------------------

export function Settings() {
  const { navMode, setNavMode, navCollapsed, setNavCollapsed } = usePrefs();
  const t = useScopedT();
  const [grpId, setGrpId] = useState("devcat");
  const grp = DICT_GROUPS.find((g) => g.id === grpId)!;

  return (
    <div>
      <h2 className="d-h2">{t("settings.title")}</h2>
      <p className="d-sub">{t("settings.sub")}</p>

      {/* Navigation card — UNCHANGED (kept intact across waves). */}
      <div className="d-card" style={{ marginBottom: 18 }}>
        <div className="d-card-hd">
          <h3>{t("settings.nav.title")}</h3>
        </div>
        <div className="d-card-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
          <div className="set-row">
            <div className="sl">
              <div className="nm">{t("settings.nav.layout")}</div>
              <div className="ds">{t("settings.nav.layoutHint")}</div>
            </div>
            <div className="view-seg" role="group" aria-label={t("settings.nav.layout")}>
              {(["side", "top"] as const).map((m) => (
                <button
                  key={m}
                  aria-pressed={navMode === m}
                  data-testid={`nav-mode-${m}`}
                  onClick={() => setNavMode(m)}
                >
                  {m === "side" ? t("settings.nav.side") : t("settings.nav.top")}
                </button>
              ))}
            </div>
          </div>
          <div className="set-row">
            <div className="sl">
              <div className="nm">{t("settings.nav.width")}</div>
              <div className="ds">{t("settings.nav.widthHint")}</div>
            </div>
            <div
              className="view-seg"
              role="group"
              aria-label={t("settings.nav.width")}
              style={{
                opacity: navMode === "top" ? 0.45 : 1,
                pointerEvents: navMode === "top" ? "none" : "auto",
              }}
            >
              {([false, true] as const).map((v) => (
                <button
                  key={String(v)}
                  aria-pressed={navCollapsed === v}
                  disabled={navMode === "top"}
                  data-testid={`nav-width-${v ? "collapsed" : "expanded"}`}
                  onClick={() => setNavCollapsed(v)}
                >
                  {v ? t("settings.nav.collapsed") : t("settings.nav.expanded")}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dictionaries split: rail + active group panel. */}
      <div className="dict-split">
        <div className="dict-rail" role="tablist" aria-label={t("settings.dict.railLabel")}>
          {DICT_GROUPS.map((g) => (
            <button
              key={g.id}
              role="tab"
              aria-selected={grpId === g.id}
              data-testid={`dict-rail-${g.id}`}
              className={`dict-railitem ${grpId === g.id ? "on" : ""}`}
              onClick={() => setGrpId(g.id)}
            >
              <span className="dri-name">{t(g.labelKey)}</span>
              {g.kind === "readonly" && <span className="dri-tag">{t("settings.dict.tag.readonly")}</span>}
              {g.kind === "unbacked" && <span className="dri-tag">{t("settings.dict.tag.soon")}</span>}
            </button>
          ))}
        </div>

        <div className="dict-main">
          {/* Remount the panel per group so its local state (dialogs, drafts)
              resets cleanly when the operator switches dictionaries. */}
          <DictPanel key={grp.id} grp={grp} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active dictionary panel — dispatches on kind
// ---------------------------------------------------------------------------

function DictPanel({ grp }: { grp: DictGroup }) {
  const t = useScopedT();

  if (grp.kind === "readonly") {
    return (
      <div className="d-card">
        <div className="d-card-hd">
          <h3>{t(grp.labelKey)}</h3>
          {grp.hintKey && <span className="dict-hint">{t(grp.hintKey)}</span>}
          <span className="sp" style={{ flex: 1 }} />
          <span className="ro-badge" data-testid="dict-readonly-badge">
            {t("settings.dict.tag.readonly")}
          </span>
        </div>
        <div className="d-card-body">
          <div className="note" data-testid="dict-readonly-note" style={{ marginBottom: 14 }}>
            <Icon d={ICONS.alert} size={16} />
            <div>{t("settings.dict.readonlyNote")}</div>
          </div>
          <table className="d-table" data-testid="dict-readonly-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>{t("settings.dict.col.value")}</th>
              </tr>
            </thead>
            <tbody>
              {TASK_STATUSES.map((s, i) => (
                <tr key={s} className="rowhover">
                  <td className="mono" style={{ color: "var(--sys-fg-mute)" }}>
                    {i + 1}
                  </td>
                  <td className="mono" style={{ fontWeight: 600 }}>
                    {s}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (grp.kind === "unbacked") {
    return (
      <div className="d-card">
        <div className="d-card-hd">
          <h3>{t(grp.labelKey)}</h3>
          <span className="sp" style={{ flex: 1 }} />
          <span className="ro-badge">{t("settings.dict.tag.soon")}</span>
        </div>
        <div className="d-card-body">
          <EmptyState
            icon={ICONS.inbox}
            title={t("settings.dict.unbacked.title")}
            hint={t("settings.dict.unbacked.hint")}
          />
          <div className="inline-alert warn" data-testid="dict-unbacked" style={{ marginTop: 8 }}>
            <Icon d={ICONS.alert} size={16} />
            <div>{t("settings.dict.unbacked.note")}</div>
          </div>
        </div>
      </div>
    );
  }

  // backed
  return grp.schema ? <CategoriesPanel grp={grp} /> : <GenericDictPanel grp={grp} />;
}

// ---------------------------------------------------------------------------
// Generic backed dictionary (stocks, downtime-categories): list + CRUD
// ---------------------------------------------------------------------------

function GenericDictPanel({ grp }: { grp: DictGroup }) {
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const t = useScopedT();
  const collection = grp.collection!;
  const mod = grp.module!;
  const rows = useCollection(collection);
  const create = useCreateRow(collection);
  const update = useUpdateRow(collection);
  const del = useDeleteRow(collection);

  const canWrite = can(mod, "write");
  const canDelete = can(mod, "delete");
  const data = useMemo(() => rows.data ?? [], [rows.data]);
  const label = t(grp.labelKey);

  // Create / rename dialog (editId === null ⇒ create).
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [isMain, setIsMain] = useState(false);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setIsMain(false);
    setOpen(true);
  };
  const openEdit = (r: Row) => {
    setEditId(r.id);
    setName(String(r.name ?? ""));
    setIsMain(Boolean((r as { isMain?: boolean }).isMain));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditId(null);
  };

  const onSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const body: Record<string, unknown> = { name: trimmed };
    if (grp.hasMainFlag) body.isMain = isMain;
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, body });
        toast(t("settings.dict.updated", { name: trimmed }), "success");
      } else {
        await create.mutateAsync(body);
        toast(t("settings.dict.added", { name: trimmed, group: label }), "success");
      }
      close();
    } catch {
      toast(t("settings.dict.writeErr"), "error");
    }
  };

  const onDelete = async (r: Row) => {
    const nm = String(r.name ?? "");
    const ok = await confirm({
      title: t("settings.dict.deleteTitle", { name: nm }),
      body: t("settings.dict.deleteBody", { group: label.toLowerCase() }),
      confirmLabel: t("settings.dict.delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(r.id);
      toast(t("settings.dict.deleted", { name: nm }), "info");
    } catch {
      toast(t("settings.dict.deleteErr"), "error");
    }
  };

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{label}</h3>
        {grp.hintKey && <span className="dict-hint">{t(grp.hintKey)}</span>}
        <span className="sp" style={{ flex: 1 }} />
        {!rows.isLoading && (
          <span className="ro-badge" data-testid="dict-count">
            {data.length}
          </span>
        )}
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="dict-add-btn" onClick={openCreate}>
            <Icon d={ICONS.plus} size={15} />
            {t("settings.dict.add")}
          </Btn>
        )}
      </div>
      <div className="d-card-body" style={{ padding: 0 }}>
        <table className="d-table" data-testid="dict-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>{t("settings.dict.col.name")}</th>
              {grp.hasMainFlag && <th style={{ width: 80 }}>{t("settings.dict.col.main")}</th>}
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {rows.isLoading && <Skeleton variant="table-row" count={3} cols={grp.hasMainFlag ? 4 : 3} />}
            {!rows.isLoading &&
              data.map((r, i) => (
                <tr key={r.id} className="rowhover">
                  <td className="mono" style={{ color: "var(--sys-fg-mute)" }}>
                    {i + 1}
                  </td>
                  <td style={{ fontWeight: 600 }} data-testid="dict-row-name">
                    {String(r.name ?? "")}
                  </td>
                  {grp.hasMainFlag && <td>{(r as { isMain?: boolean }).isMain ? t("settings.dict.yes") : "—"}</td>}
                  <td style={{ textAlign: "right" }}>
                    {canWrite && (
                      <button
                        className="row-icon-btn"
                        title={t("settings.dict.edit")}
                        aria-label={t("settings.dict.editNamed", { name: String(r.name ?? "") })}
                        data-testid={`dict-edit-${r.id}`}
                        onClick={() => openEdit(r)}
                      >
                        <Icon d={ICONS.cog} size={15} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="row-icon-btn"
                        title={t("settings.dict.delete")}
                        aria-label={t("settings.dict.deleteNamed", { name: String(r.name ?? "") })}
                        data-testid={`dict-delete-${r.id}`}
                        onClick={() => void onDelete(r)}
                      >
                        <Icon d={ICONS.trash} size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!rows.isLoading && data.length === 0 && (
          <div style={{ padding: 8 }}>
            <EmptyState
              compact
              title={t("settings.dict.empty", { group: label.toLowerCase() })}
              hint={canWrite ? t("settings.dict.emptyHint") : undefined}
              action={canWrite ? { label: t("settings.dict.add"), onClick: openCreate } : undefined}
            />
          </div>
        )}
      </div>

      {open && (
        <Modal
          title={editId ? t("settings.dict.editTitle", { group: label }) : t("settings.dict.newTitle", { group: label })}
          onClose={close}
          testId="dict-dialog"
          width={420}
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="dict-name-input">{t("settings.dict.col.name")}</label>
              <div className="field-ctrl">
                <input
                  id="dict-name-input"
                  data-testid="dict-name-input"
                  value={name}
                  placeholder={t("settings.dict.namePlaceholder", { group: label.toLowerCase() })}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onSubmit();
                  }}
                />
              </div>
            </div>
            {grp.hasMainFlag && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13, color: "var(--sys-fg-2)" }}>
                <button
                  className={`toggle ${isMain ? "on" : ""}`}
                  role="switch"
                  aria-checked={isMain}
                  aria-label={t("settings.dict.col.main")}
                  data-testid="dict-main-toggle"
                  onClick={() => setIsMain((v) => !v)}
                  style={toggleStyle(isMain)}
                >
                  <span className="knob" />
                </button>
                {t("settings.dict.mainHint")}
              </label>
            )}
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              data-testid="dict-save"
              disabled={!name.trim() || create.isPending || update.isPending}
              onClick={() => void onSubmit()}
            >
              {editId ? t("settings.dict.save") : t("settings.dict.create")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device categories + custom-field schema editor (the centrepiece)
// ---------------------------------------------------------------------------

function CategoriesPanel({ grp }: { grp: DictGroup }) {
  const { can } = useAuth();
  const { toast, confirm } = useChrome();
  const t = useScopedT();
  const cats = useCollection("categories");
  const createCat = useCreateRow("categories");
  const delCat = useDeleteRow("categories");
  // custom-field-defs are gated by the SAME module as categories (dev).
  const canWrite = can("dev", "write");
  const canDelete = can("dev", "delete");

  const data = useMemo(() => cats.data ?? [], [cats.data]);
  const [open, setOpen] = useState<string | null>(null);
  const [newCat, setNewCat] = useState("");

  const onAddCat = async () => {
    const nm = newCat.trim();
    if (!nm) return;
    try {
      await createCat.mutateAsync({ name: nm });
      toast(t("settings.dict.added", { name: nm, group: t(grp.labelKey) }), "success");
      setNewCat("");
    } catch {
      toast(t("settings.dict.writeErr"), "error");
    }
  };

  const onDeleteCat = async (cat: Row) => {
    const nm = String(cat.name ?? "");
    const ok = await confirm({
      title: t("settings.dict.deleteTitle", { name: nm }),
      body: t("settings.cf.deleteCatBody"),
      confirmLabel: t("settings.dict.delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await delCat.mutateAsync(cat.id);
      toast(t("settings.dict.deleted", { name: nm }), "info");
      if (open === cat.id) setOpen(null);
    } catch {
      toast(t("settings.dict.deleteErr"), "error");
    }
  };

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{t(grp.labelKey)}</h3>
        {grp.hintKey && <span className="dict-hint">{t(grp.hintKey)}</span>}
        <span className="sp" style={{ flex: 1 }} />
        {!cats.isLoading && (
          <span className="ro-badge" data-testid="dict-count">
            {data.length}
          </span>
        )}
      </div>
      <div className="d-card-body">
        {canWrite && (
          <div className="dict-add" style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <div className="field-ctrl" style={{ flex: 1 }}>
              <input
                aria-label={t("settings.cf.newCatLabel")}
                data-testid="cat-add-name"
                placeholder={t("settings.cf.newCatPlaceholder")}
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void onAddCat();
                }}
              />
            </div>
            <Btn
              variant="primary"
              size="sm"
              data-testid="cat-add-btn"
              disabled={!newCat.trim() || createCat.isPending}
              onClick={() => void onAddCat()}
            >
              <Icon d={ICONS.plus} size={15} />
              {t("settings.dict.add")}
            </Btn>
          </div>
        )}

        {cats.isLoading ? (
          <Skeleton variant="text" count={4} />
        ) : data.length === 0 ? (
          <EmptyState
            compact
            title={t("settings.cf.noCats")}
            hint={canWrite ? t("settings.cf.noCatsHint") : undefined}
          />
        ) : (
          <div className="dict-cats">
            {data.map((cat) => (
              <div className={`dict-cat ${open === cat.id ? "open" : ""}`} key={cat.id}>
                <div className="dict-cat-hd" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    data-testid={`cat-hd-${cat.id}`}
                    aria-expanded={open === cat.id}
                    onClick={() => setOpen((o) => (o === cat.id ? null : cat.id))}
                    style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, border: 0, background: "none", font: "inherit", color: "inherit", cursor: "pointer", padding: "10px 4px", textAlign: "left" }}
                  >
                    <Icon
                      d={ICONS.chevron}
                      size={15}
                      style={{ transform: open === cat.id ? "rotate(90deg)" : "none", transition: "transform .2s", color: "var(--sys-fg-3)" }}
                    />
                    <span className="dcat-name" style={{ fontWeight: 700 }}>
                      {String(cat.name ?? "")}
                    </span>
                  </button>
                  {canDelete && (
                    <button
                      className="row-icon-btn"
                      title={t("settings.dict.delete")}
                      aria-label={t("settings.dict.deleteNamed", { name: String(cat.name ?? "") })}
                      data-testid={`cat-delete-${cat.id}`}
                      onClick={() => void onDeleteCat(cat)}
                    >
                      <Icon d={ICONS.trash} size={15} />
                    </button>
                  )}
                </div>
                {open === cat.id && (
                  <SchemaEditor categoryId={cat.id} canWrite={canWrite} canDelete={canDelete} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom-field schema editor for ONE device category.
//
// Wire shape (DB-exact custom_field_defs columns only — no invented columns):
//   POST  /custom-field-defs  { categoryId, key, label, type, required, suffix? }
//   PATCH /custom-field-defs/:id { label?, type?, required?, suffix? }
//   DELETE /custom-field-defs/:id
// `key` is derived from the label on create + frozen thereafter (it keys the
// devices.custom_fields JSONB; renaming it would orphan existing values).
// ---------------------------------------------------------------------------

interface FieldDef extends Row {
  categoryId?: string;
  key?: string;
  label?: string;
  type?: FieldType;
  required?: boolean;
  suffix?: string | null;
}

function SchemaEditor({
  categoryId,
  canWrite,
  canDelete,
}: {
  categoryId: string;
  canWrite: boolean;
  canDelete: boolean;
}) {
  const { toast, confirm } = useChrome();
  const t = useScopedT();
  const defs = useCollection<FieldDef>("custom-field-defs");
  const create = useCreateRow("custom-field-defs");
  const update = useUpdateRow("custom-field-defs");
  const del = useDeleteRow("custom-field-defs");
  const addOpt = useAddCustomFieldOption();

  const mine = useMemo(
    () => (defs.data ?? []).filter((d) => d.categoryId === categoryId),
    [defs.data, categoryId],
  );

  // Field dialog (editId === null ⇒ add).
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const [required, setRequired] = useState(false);
  const [suffix, setSuffix] = useState("");
  // Select-option values. For an EXISTING def, options are loaded + persisted
  // live by <PersistedOptionsEditor> (keyed on editId). This local buffer holds
  // only the pending options for a NEW def, which are persisted right after the
  // def is created (a def must exist before its options can be anchored to it).
  const [options, setOptions] = useState<string[]>([]);
  const [optDraft, setOptDraft] = useState("");

  const openAdd = () => {
    setEditId(null);
    setLabel("");
    setType("text");
    setRequired(false);
    setSuffix("");
    setOptions([]);
    setOptDraft("");
    setOpen(true);
  };
  const openEdit = (f: FieldDef) => {
    setEditId(f.id);
    setLabel(String(f.label ?? ""));
    setType((FIELD_TYPES as readonly string[]).includes(String(f.type)) ? (f.type as FieldType) : "text");
    setRequired(Boolean(f.required));
    setSuffix(String(f.suffix ?? ""));
    setOptions([]);
    setOptDraft("");
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditId(null);
  };

  const addOption = () => {
    const v = optDraft.trim();
    if (!v || options.includes(v)) return;
    setOptions((o) => [...o, v]);
    setOptDraft("");
  };
  const removeOption = (v: string) => setOptions((o) => o.filter((x) => x !== v));

  const onSubmit = async () => {
    const lab = label.trim();
    if (!lab) return;
    // suffix is a real nullable column; send null when blank.
    const sfx = suffix.trim() ? suffix.trim() : null;
    try {
      if (editId) {
        // `key` is intentionally NOT patched — it anchors existing JSONB values.
        await update.mutateAsync({
          id: editId,
          body: { label: lab, type, required, suffix: sfx },
        });
        toast(t("settings.cf.updated", { name: lab }), "success");
      } else {
        const createdRow = await create.mutateAsync({
          categoryId,
          key: keyFromLabel(lab),
          label: lab,
          type,
          required,
          suffix: sfx,
        });
        // Persist the pending SELECT options against the freshly-created def
        // (options must anchor to an existing def id). Best-effort per option.
        const newId = (createdRow as { id?: string }).id;
        if (type === "select" && newId && options.length) {
          for (const value of options) {
            await addOpt.mutateAsync({ fieldId: newId, value });
          }
        }
        toast(t("settings.cf.added", { name: lab }), "success");
      }
      close();
    } catch {
      toast(t("settings.cf.writeErr"), "error");
    }
  };

  const onDelete = async (f: FieldDef) => {
    const nm = String(f.label ?? f.name ?? "");
    const ok = await confirm({
      title: t("settings.cf.deleteTitle", { name: nm }),
      body: t("settings.cf.deleteBody"),
      confirmLabel: t("settings.dict.delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(f.id);
      toast(t("settings.cf.deleted", { name: nm }), "info");
    } catch {
      toast(t("settings.dict.deleteErr"), "error");
    }
  };

  return (
    <div className="dict-cat-body" style={{ padding: "4px 4px 14px 26px" }}>
      <div className="dcat-sec" style={{ fontWeight: 700, fontSize: 12, color: "var(--sys-fg-2)", margin: "8px 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
        {t("settings.cf.title")}
        <span className="sp" style={{ flex: 1 }} />
        {canWrite && (
          <Btn variant="secondary" size="sm" data-testid="cf-add" onClick={openAdd}>
            <Icon d={ICONS.plus} size={14} />
            {t("settings.cf.addField")}
          </Btn>
        )}
      </div>

      {defs.isLoading ? (
        <Skeleton variant="text" count={2} />
      ) : mine.length === 0 ? (
        <EmptyState compact title={t("settings.cf.none")} hint={canWrite ? t("settings.cf.noneHint") : undefined} />
      ) : (
        <div className="dcat-chips" style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="cf-chips">
          {mine.map((f) => (
            <span
              className="dcat-chip"
              key={f.id}
              data-testid={`cf-chip-${f.id}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--sys-bg-inset)", border: "1px solid var(--sys-border)", borderRadius: 999, padding: "4px 6px 4px 12px", fontSize: 12.5 }}
            >
              <span style={{ fontWeight: 600 }}>{String(f.label ?? f.name ?? "")}</span>
              {f.required ? <span aria-label={t("settings.cf.required")} title={t("settings.cf.required")} style={{ color: "var(--status-critical-ink)" }}>*</span> : null}
              <small className="mono" style={{ color: "var(--sys-fg-mute)" }}>{String(f.type ?? "")}</small>
              {canWrite && (
                <button
                  className="row-icon-btn"
                  aria-label={t("settings.cf.editNamed", { name: String(f.label ?? "") })}
                  data-testid={`cf-edit-${f.id}`}
                  style={{ width: 22, height: 22 }}
                  onClick={() => openEdit(f)}
                >
                  <Icon d={ICONS.cog} size={12} />
                </button>
              )}
              {canDelete && (
                <button
                  className="row-icon-btn"
                  aria-label={t("settings.cf.deleteNamed", { name: String(f.label ?? "") })}
                  data-testid={`cf-delete-${f.id}`}
                  style={{ width: 22, height: 22 }}
                  onClick={() => void onDelete(f)}
                >
                  <Icon d={ICONS.x} size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {open && (
        <Modal
          title={editId ? t("settings.cf.editTitle") : t("settings.cf.newTitle")}
          icon={ICONS.cog}
          onClose={close}
          testId="cf-dialog"
          width={460}
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="cf-label">{t("settings.cf.label")}</label>
              <div className="field-ctrl">
                <input
                  id="cf-label"
                  data-testid="cf-label"
                  value={label}
                  placeholder={t("settings.cf.labelPlaceholder")}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
            </div>

            <div className="d-field">
              <label htmlFor="cf-type">{t("settings.cf.type")}</label>
              <div className="field-ctrl">
                <select
                  id="cf-type"
                  data-testid="cf-type"
                  value={type}
                  onChange={(e) => setType(e.target.value as FieldType)}
                  style={selectStyle}
                >
                  {FIELD_TYPES.map((ft) => (
                    <option key={ft} value={ft}>
                      {ft}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="d-field">
              <label htmlFor="cf-suffix">{t("settings.cf.suffix")}</label>
              <div className="field-ctrl">
                <input
                  id="cf-suffix"
                  data-testid="cf-suffix"
                  value={suffix}
                  placeholder={t("settings.cf.suffixPlaceholder")}
                  onChange={(e) => setSuffix(e.target.value)}
                />
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 13, color: "var(--sys-fg-2)" }}>
              <button
                className={`toggle ${required ? "on" : ""}`}
                role="switch"
                aria-checked={required}
                aria-label={t("settings.cf.required")}
                data-testid="cf-required"
                onClick={() => setRequired((v) => !v)}
                style={toggleStyle(required)}
              >
                <span className="knob" />
              </button>
              {t("settings.cf.requiredHint")}
            </label>

            {type === "select" && editId && (
              // EXISTING def: load + persist real options live against the server.
              <PersistedOptionsEditor fieldId={editId} canWrite={canWrite} />
            )}

            {type === "select" && !editId && (
              // NEW def: buffer options locally; they are persisted right after
              // the def is created (in onSubmit) since options anchor to a def id.
              <div className="d-field" data-testid="cf-options">
                <label>{t("settings.cf.options")}</label>
                <div className="dcat-chips" style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0" }}>
                  {options.length === 0 && <small className="ad-none">{t("settings.cf.optionsEmpty")}</small>}
                  {options.map((o) => (
                    <span
                      key={o}
                      className="dcat-chip"
                      data-testid={`cf-opt-${o}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--sys-bg-inset)", border: "1px solid var(--sys-border)", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: 12 }}
                    >
                      {o}
                      <button
                        className="row-icon-btn"
                        aria-label={t("settings.cf.removeOption", { value: o })}
                        style={{ width: 20, height: 20 }}
                        onClick={() => removeOption(o)}
                      >
                        <Icon d={ICONS.x} size={11} />
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div className="field-ctrl" style={{ flex: 1 }}>
                    <input
                      aria-label={t("settings.cf.optionValue")}
                      data-testid="cf-opt-input"
                      value={optDraft}
                      placeholder={t("settings.cf.optionPlaceholder")}
                      onChange={(e) => setOptDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                  </div>
                  <Btn variant="ghost" size="sm" data-testid="cf-opt-add" disabled={!optDraft.trim()} onClick={addOption}>
                    <Icon d={ICONS.plus} size={13} />
                    {t("settings.cf.addOption")}
                  </Btn>
                </div>
              </div>
            )}
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              data-testid="cf-save"
              disabled={!label.trim() || create.isPending || update.isPending}
              onClick={() => void onSubmit()}
            >
              {editId ? t("settings.cf.save") : t("settings.cf.addField")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Live SELECT-option editor for an EXISTING custom-field def. Loads the real
 * persisted options (GET /custom-field-defs/:id/options) and persists add/remove
 * immediately (POST/DELETE). Tenant-scoped server-side via the parent def.
 */
function PersistedOptionsEditor({ fieldId, canWrite }: { fieldId: string; canWrite: boolean }) {
  const t = useScopedT();
  const { toast } = useChrome();
  const opts = useCustomFieldOptions(fieldId);
  const addOpt = useAddCustomFieldOption();
  const delOpt = useDeleteCustomFieldOption();
  const [draft, setDraft] = useState("");

  const rows = opts.data ?? [];
  const add = async () => {
    const v = draft.trim();
    if (!v || rows.some((r) => r.value === v)) return;
    try {
      await addOpt.mutateAsync({ fieldId, value: v });
      setDraft("");
    } catch {
      toast(t("settings.cf.writeErr"), "error");
    }
  };
  const remove = async (optionId: string) => {
    try {
      await delOpt.mutateAsync({ fieldId, optionId });
    } catch {
      toast(t("settings.cf.writeErr"), "error");
    }
  };

  return (
    <div className="d-field" data-testid="cf-options">
      <label>{t("settings.cf.options")}</label>
      <div className="dcat-chips" style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0" }}>
        {opts.isLoading && <Skeleton width="120px" />}
        {!opts.isLoading && rows.length === 0 && <small className="ad-none">{t("settings.cf.optionsEmpty")}</small>}
        {rows.map((o) => (
          <span
            key={o.id}
            className="dcat-chip"
            data-testid={`cf-opt-${o.value}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--sys-bg-inset)", border: "1px solid var(--sys-border)", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: 12 }}
          >
            {o.value}
            {canWrite && (
              <button
                className="row-icon-btn"
                aria-label={t("settings.cf.removeOption", { value: o.value })}
                style={{ width: 20, height: 20 }}
                disabled={delOpt.isPending}
                onClick={() => void remove(o.id)}
              >
                <Icon d={ICONS.x} size={11} />
              </button>
            )}
          </span>
        ))}
      </div>
      {canWrite && (
        <div style={{ display: "flex", gap: 8 }}>
          <div className="field-ctrl" style={{ flex: 1 }}>
            <input
              aria-label={t("settings.cf.optionValue")}
              data-testid="cf-opt-input"
              value={draft}
              placeholder={t("settings.cf.optionPlaceholder")}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void add();
                }
              }}
            />
          </div>
          <Btn variant="ghost" size="sm" data-testid="cf-opt-add" disabled={!draft.trim() || addOpt.isPending} onClick={() => void add()}>
            <Icon d={ICONS.plus} size={13} />
            {t("settings.cf.addOption")}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Derive the JSONB key from a label: lowercased, non-alnum → underscore. */
function keyFromLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}
